/**
 * wind_physics — how hard the weather pushes back when you walk into it.
 *
 * WHY THIS IS AN INPUT SCALE AND NOT A SPEED MODIFIER. Player movement is
 * SERVER-INTEGRATED: `moveSteps2` computes `posX + moveX · speedFor(mode) · dt`
 * itself. A client that simply walked slower would be dragged back up to the
 * server's answer by reconciliation every tick. But the reducer normalises the
 * intent vector ONLY when its length exceeds 1:
 *
 *     const len = Math.hypot(ix, iz);
 *     if (len > 1) { ix /= len; iz /= len; }
 *
 * so a SUB-UNIT intent integrates proportionally on the server. Shortening the
 * intent vector therefore slows the player on both sides by construction, with
 * no reducer change, no shared weather model, and no desync. This is the same
 * trick the collision-resolved send already uses (AtlasAvatarModule: "Sub-unit
 * intent integrates proportionally server-side").
 *
 * It is also cheat-neutral in the right direction: the factor can only ever
 * REDUCE the vector, and the server clamps anything above 1, so a client that
 * ignores the wind is exactly as fast as one that never had wind — it cannot
 * gain an advantage, only decline a disadvantage. That is the same trust level
 * as every other movement input here.
 *
 * DIRECTION CONVENTION: the weather's wind vector points the way the wind
 * BLOWS TOWARD (WeatherModule scrolls the dust field along it), so walking
 * into the wind is a NEGATIVE dot product with it.
 */

/**
 * Peak fraction of speed lost walking straight into a full-strength wind, by
 * precipitation kind. Only the sandstorm resists meaningfully: driving sand
 * is the one weather here with enough momentum to lean on you. Rain and snow
 * are listed at 0 so the table is the single place to change that.
 */
export const WIND_DRAG_BY_KIND: Record<string, number> = {
  dust: 0.28,   // sandstorm
  none: 0,
  rain: 0,
  snow: 0,
};

/** Never scale the intent below this — the wind slows you, it never pins you. */
export const WIND_DRAG_FLOOR = 0.55;

export interface WindSample {
  /** Unit-ish direction the wind blows TOWARD. */
  x: number;
  z: number;
  /** 0..1. */
  strength: number;
  /** Precipitation kind ('dust' = sandstorm). */
  kind: string;
}

/**
 * The factor to multiply the player's intent vector by, in (0, 1].
 *
 * 1 means no resistance: not moving, no wind, a kind that does not resist, or
 * moving with or across the wind. The drag scales with how directly the move
 * opposes the wind (the cosine, so a quartering wind resists partially) and
 * with the wind's strength.
 */
export function windDragFactor(moveX: number, moveZ: number, wind: WindSample | null): number {
  if (!wind) return 1;
  const maxDrag = WIND_DRAG_BY_KIND[wind.kind] ?? 0;
  if (maxDrag <= 0) return 1;
  const strength = Math.min(1, Math.max(0, wind.strength));
  if (strength <= 0) return 1;
  const moveLen = Math.hypot(moveX, moveZ);
  const windLen = Math.hypot(wind.x, wind.z);
  if (moveLen <= 1e-6 || windLen <= 1e-6) return 1;
  // Headwind component: +1 walking straight into it, 0 across, −1 with it.
  const head = -((moveX * wind.x + moveZ * wind.z) / (moveLen * windLen));
  if (head <= 0) return 1; // with the wind or across it — no push-back, and no free speed
  const factor = 1 - maxDrag * strength * head;
  return Math.min(1, Math.max(WIND_DRAG_FLOOR, factor));
}
